import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createTheme } from 'mineral-ui/themes';
import { withTheme } from 'emotion-theming';
import Icon from 'mineral-ui/Icon';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  Flex,
  FormField,
  FormFieldset,
  Modal,
  Page,
  Select,
  Tabs,
  Text,
  TextInput,
} from '../../components';
import { updateTheme } from '../../modules/theme';
import GlobalBaseTheme from '../../globalBaseTheme';
import {
  getClient,
  getFontSizeOverride,
  getThemeWithoutOverrides,
} from '../../modules/selectors';
import api from '../../utils/services';


const propTypes = {
  client: PropTypes.shape({
    clientName: PropTypes.string.isRequired,
  }).isRequired,
  fontSizeOverride: PropTypes.string,
  theme: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  themeWithoutOverrides: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  dispatch: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      carrierId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

const defaultProps = {
  fontSizeOverride: null,
};

const ColorIcon = ({ color }) => (
  // Cannot just set the icon color since the form field overrides it
  <Icon>
    <svg viewBox="0 0 16 16">
      <rect x="0" y="0" width="100%" height="100%" style={{ stroke: '#000000', fill: color }} />
    </svg>
  </Icon>
);
ColorIcon.propTypes = {
  color: PropTypes.string.isRequired,
};

class Theme extends Component {
  constructor(props) {
    super(props);

    this.state = {
      palette: 'blue',
      customTheme: this.currentThemeValues(),
      errorMessage: null,
      saveAllOpen: false,
      saving: false,
    };
  }

  /* If a newTheme is provided it will use that
   * otherwise it will grab the values from store and context
   */
  currentThemeValues = (newTheme) => {
    const { theme, themeWithoutOverrides } = this.props;

    let themeValue;
    if (newTheme) {
      themeValue = key => (
        newTheme[key] || themeWithoutOverrides[key] || ''
      );
    } else {
      themeValue = key => (
        themeWithoutOverrides[key] || theme[key] || ''
      );
    }

    /* An update to the object should have a corresponding update to
     * (this - currentThemeValues return)
     * saveTheme deconstructor assignment
     * saveTheme dispatch
     */
    return {
      // Background
      backgroundColor: themeValue('backgroundColor'),
      backgroundColorPage: themeValue('backgroundColor_page'),
      backgroundColorThemePrimary: themeValue('backgroundColor_themePrimary'),
      backgroundColorThemePrimaryActive: themeValue('backgroundColor_themePrimary_active'),
      backgroundColorThemePrimaryFocus: themeValue('backgroundColor_themePrimary_focus'),
      backgroundColorThemePrimaryHover: themeValue('backgroundColor_themePrimary_hover'),
      // Border
      borderColor: themeValue('borderColor'),
      borderColorTheme: themeValue('borderColor_theme'),
      borderColorThemeActive: themeValue('borderColor_theme_active'),
      borderColorThemeFocus: themeValue('borderColor_theme_focus'),
      borderColorThemeHover: themeValue('borderColor_theme_hover'),
      // Heading
      h1Color: themeValue('h1_color'),
      h2Color: themeValue('h2_color'),
      h3Color: themeValue('h3_color'),
      h4Color: themeValue('h4_color'),
      h5Color: themeValue('h5_color'),
      h6Color: themeValue('h6_color'),
      h1FontSize: themeValue('h1_fontSize'),
      h2FontSize: themeValue('h2_fontSize'),
      h3FontSize: themeValue('h3_fontSize'),
      h4FontSize: themeValue('h4_fontSize'),
      h5FontSize: themeValue('h5_fontSize'),
      h6FontSize: themeValue('h6_fontSize'),
      h1FontWeight: themeValue('h1_fontWeight'),
      h2FontWeight: themeValue('h2_fontWeight'),
      h3FontWeight: themeValue('h3_fontWeight'),
      h4FontWeight: themeValue('h4_fontWeight'),
      h5FontWeight: themeValue('h5_fontWeight'),
      h6FontWeight: themeValue('h6_fontWeight'),
      // Typography
      color: themeValue('color'),
      colorTheme: themeValue('color_theme'),
      colorThemeFocus: themeValue('color_theme_focus'),
      colorThemeHover: themeValue('color_theme_hover'),
      fontFamily: themeValue('fontFamily'),
      fontSizeBase: themeValue('fontSize_base'),
      fontSizeUI: themeValue('fontSize_ui'),
      fontSizeProse: themeValue('fontSize_prose'),
      lineHeight: themeValue('lineHeight'),
      lineHeightHeading: themeValue('lineHeight_heading'),
      lineHeightProse: themeValue('lineHeight_prose'),
      // Space
      spaceInlineSM: themeValue('space_inline_sm'),
      spaceInlineMD: themeValue('space_inline_md'),
      spaceInlineLG: themeValue('space_inline_lg'),
      spaceStackSM: themeValue('space_stack_sm'),
      spaceStackMD: themeValue('space_stack_md'),
      spaceStackLG: themeValue('space_stack_lg'),
    };
  }

  handlePaletteChange = ({ value }) => {
    const { dispatch } = this.props;
    let customTheme = createTheme({
      colors: {
        theme: value,
      },
    });
    customTheme = {
      ...GlobalBaseTheme,
      ...customTheme,
    };
    dispatch(updateTheme(customTheme));
    this.setState({
      customTheme: this.currentThemeValues(customTheme),
    });
  }

  handleCustomThemeChange = (event) => {
    const { target: { name, value } } = event;

    this.setState(state => ({
      customTheme: {
        ...state.customTheme,
        [name]: value,
      },
    }));
  }

  handleSubmit = (event) => {
    event.preventDefault();
    this.saveTheme();
  }

  saveTheme = (allCarriers = false) => {
    const { dispatch, match: { params: { carrierId } }, theme } = this.props;

    this.setState({ errorMessage: null, saving: true });

    /* An update to the object should have a corresponding update to
     * currentThemeValues
     * (this - saveTheme deconstructor assignment)
     * saveTheme dispatch
     */
    const {
      customTheme: {
        // Background
        backgroundColor,
        backgroundColorPage,
        backgroundColorThemePrimary,
        backgroundColorThemePrimaryActive,
        backgroundColorThemePrimaryFocus,
        backgroundColorThemePrimaryHover,
        // Border
        borderColor,
        borderColorTheme,
        borderColorThemeActive,
        borderColorThemeFocus,
        borderColorThemeHover,
        // Heading
        h1Color,
        h2Color,
        h3Color,
        h4Color,
        h5Color,
        h6Color,
        h1FontSize,
        h2FontSize,
        h3FontSize,
        h4FontSize,
        h5FontSize,
        h6FontSize,
        h1FontWeight,
        h2FontWeight,
        h3FontWeight,
        h4FontWeight,
        h5FontWeight,
        h6FontWeight,
        // Typography
        color,
        colorTheme,
        colorThemeFocus,
        colorThemeHover,
        fontFamily,
        fontSizeBase,
        fontSizeUI,
        fontSizeProse,
        lineHeight,
        lineHeightHeading,
        lineHeightProse,
        // Space
        spaceInlineSM,
        spaceInlineMD,
        spaceInlineLG,
        spaceStackSM,
        spaceStackMD,
        spaceStackLG,
      },
    } = this.state;

    /* An update to the object should have a corresponding update to
     * currentThemeValues
     * saveTheme deconstructor assignment
     * (this - saveTheme dispatch)
     */
    const customThemeOverrides = {
      // Background
      backgroundColor,
      backgroundColor_page: backgroundColorPage,
      backgroundColor_themePrimary: backgroundColorThemePrimary,
      backgroundColor_themePrimary_active: backgroundColorThemePrimaryActive,
      backgroundColor_themePrimary_focus: backgroundColorThemePrimaryFocus,
      backgroundColor_themePrimary_hover: backgroundColorThemePrimaryHover,
      // Border
      borderColor,
      borderColor_theme: borderColorTheme,
      borderColor_theme_active: borderColorThemeActive,
      borderColor_theme_focus: borderColorThemeFocus,
      borderColor_theme_hover: borderColorThemeHover,
      // Heading
      h1_color: h1Color,
      h2_color: h2Color,
      h3_color: h3Color,
      h4_color: h4Color,
      h5_color: h5Color,
      h6_color: h6Color,
      h1_fontSize: h1FontSize,
      h2_fontSize: h2FontSize,
      h3_fontSize: h3FontSize,
      h4_fontSize: h4FontSize,
      h5_fontSize: h5FontSize,
      h6_fontSize: h6FontSize,
      h1_fontWeight: h1FontWeight,
      h2_fontWeight: h2FontWeight,
      h3_fontWeight: h3FontWeight,
      h4_fontWeight: h4FontWeight,
      h5_fontWeight: h5FontWeight,
      h6_fontWeight: h6FontWeight,
      // Typography
      color,
      color_theme: colorTheme,
      color_theme_focus: colorThemeFocus,
      color_theme_hover: colorThemeHover,
      fontFamily,
      fontSize_base: fontSizeBase,
      fontSize_ui: fontSizeUI,
      fontSize_prose: fontSizeProse,
      lineHeight,
      lineHeight_heading: lineHeightHeading,
      lineHeight_prose: lineHeightProse,
      // Space
      space_inline_lg: spaceInlineLG,
      space_inline_md: spaceInlineMD,
      space_inline_sm: spaceInlineSM,
      space_stack_lg: spaceStackLG,
      space_stack_md: spaceStackMD,
      space_stack_sm: spaceStackSM,
    };
    dispatch(updateTheme(customThemeOverrides));

    const themeValue = JSON.stringify({ ...theme, ...customThemeOverrides });
    let carriersToUpdate;
    // In case an event is passed to this function by accident, explicitly check for true
    if (allCarriers === true) {
      carriersToUpdate = api.getCarriers(1000);
    } else {
      carriersToUpdate = Promise.resolve([{ id: carrierId }]);
    }

    return carriersToUpdate.then((carriers) => {
      const updateRequests = carriers.map(carrierIdToUpdate => (
        api.updateTheme({ carrierId: carrierIdToUpdate.id, Value: themeValue })
      ));

      return Promise.all(updateRequests);
    }).then(() => {
      this.setState({ saving: false });
    }).catch((error) => {
      this.setState({
        errorMessage: 'Failed to save theme. Please try again.',
        saving: false,
      });
      console.log(`Failed to save theme: ${error}`);
    });
  }

  ColorFormField = ({ caption, label, name }) => {
    const { customTheme } = this.state;
    const variant = customTheme[name].match(/^#[0-9A-Fa-f]{6}$/) ? undefined : 'warning';

    return (
      <Box marginBottom="1em">
        <FormField
          label={label}
          caption={caption}
          input={TextInput}
          name={name}
          value={customTheme[name]}
          iconStart={<ColorIcon color={customTheme[name]} />}
          variant={variant}
          onChange={this.handleCustomThemeChange}
        />
      </Box>
    );
  }

  TextFormField = ({
    caption,
    secondaryText,
    label,
    name,
  }) => {
    const { customTheme } = this.state;
    const {
      themeWithoutOverrides: { fontSize_base: fontSizeBase },
      fontSizeOverride,
    } = this.props;
    const fontSizeWarning = fontSizeOverride && fontSizeOverride !== fontSizeBase;
    const fontSizeWarningMessage = 'WARNING: You currently have the app resized, so font size changes will not take effect until the page is refreshed or is resized back to normal. ';
    return (
      <Box marginBottom="1em">
        <FormField
          label={label}
          caption={`${fontSizeWarning ? fontSizeWarningMessage : ''}${caption || ''}`}
          secondaryText={secondaryText}
          variant={fontSizeWarning ? 'warning' : undefined}
          input={TextInput}
          name={name}
          value={customTheme[name]}
          onChange={this.handleCustomThemeChange}
        />
      </Box>
    );
  }

  render() {
    const {
      palette,
      errorMessage,
      saveAllOpen,
      saving,
    } = this.state;
    const {
      client,
    } = this.props;


    const paletteOptions = [{
      text: 'Default (Blue)',
      value: 'blue',
    }, {
      text: 'Bronze',
      value: 'bronze',
    }, {
      text: 'Dusk',
      value: 'dusk',
    }, {
      text: 'Gray',
      value: 'gray',
    }, {
      text: 'Green',
      value: 'green',
    }, {
      text: 'Indigo',
      value: 'indigo',
    }, {
      text: 'Magenta',
      value: 'magenta',
    }, {
      text: 'Purple',
      value: 'purple',
    }, {
      text: 'Red',
      value: 'red',
    }, {
      text: 'Slate',
      value: 'slate',
    }, {
      text: 'Sky',
      value: 'sky',
    }, {
      text: 'Teal',
      value: 'teal',
    }];

    const { ColorFormField, TextFormField } = this;

    return (
      <Page title="Theme Customization">
        <Text as="h2">Preconfigured Palettes</Text>
        <Card>
          <Card.Block>
            <FormField
              label="Color Scheme"
              input={Select}
              data={paletteOptions}
              value={palette}
              onChange={this.handlePaletteChange}
              caption="Warning! Selecting a preconfigured theme will reset the values below."
              variant="warning"
            />
            <Flex paddingTop="1em">
              <Flex.Item marginStart="auto">
                <Button primary onClick={() => this.saveTheme()} loading={saving}>Save</Button>
              </Flex.Item>
            </Flex>
          </Card.Block>
        </Card>
        <Box paddingTop="1em" />
        <Text as="h2">Custom Overrides</Text>
        <Card>
          <Card.Block>
            {errorMessage && <Text color="red">{errorMessage}</Text>}
            <form onSubmit={this.handleSubmit}>
              <Tabs label="Custom Theme Values">
                <Tabs.Tab title="Background">
                  <FormFieldset legend="General">
                    <ColorFormField
                      label="Background Color"
                      caption="E.G. Background color non-primary buttons."
                      name="backgroundColor"
                    />
                    <ColorFormField
                      label="Background Color Page"
                      caption="E.G. Background color of page canvas. Used to provide contrast to white background."
                      name="backgroundColorPage"
                    />
                  </FormFieldset>
                  <FormFieldset legend="Theme">
                    <ColorFormField
                      label="Background Color Theme Primary"
                      caption="E.G. Color of primary buttons"
                      name="backgroundColorThemePrimary"
                    />
                    <ColorFormField
                      label="Background Color Primary Active"
                      caption="E.G. Color of primary buttons when activated (mouse button down)"
                      name="backgroundColorThemePrimaryActive"
                    />
                    <ColorFormField
                      label="Background Color Primary Focus"
                      caption="E.G. Color of primary buttons when focused (selected via tab or after click)"
                      name="backgroundColorThemePrimaryFocus"
                    />
                    <ColorFormField
                      label="Background Color Primary Hover"
                      caption="E.G. Color of primary buttons when mouse hovering"
                      name="backgroundColorThemePrimaryHover"
                    />
                  </FormFieldset>
                </Tabs.Tab>
                <Tabs.Tab title="Border">
                  <FormFieldset legend="General">
                    <ColorFormField
                      label="Border Color"
                      caption="E.G. Border around a form field"
                      name="borderColor"
                    />
                  </FormFieldset>
                  <FormFieldset legend="Theme">
                    <ColorFormField
                      label="Border Color Theme"
                      caption="E.G. Radio button ring color"
                      name="borderColorTheme"
                    />
                    <ColorFormField
                      label="Border Color Theme Active"
                      caption="E.G. Border when a form field is activated (mouse button down)"
                      name="borderColorThemeActive"
                    />
                    <ColorFormField
                      label="Border Color Theme Focus"
                      caption="E.G. Border when a form field is selected"
                      name="borderColorThemeFocus"
                    />
                    <ColorFormField
                      label="Border Color Theme Hover"
                      caption="E.G. Border when mouse hovering a form field"
                      name="borderColorThemeHover"
                    />
                  </FormFieldset>
                </Tabs.Tab>
                <Tabs.Tab title="Heading">
                  <FormFieldset legend="Color">
                    <ColorFormField
                      label="Header 1 Color"
                      name="h1Color"
                    />
                    <ColorFormField
                      label="Header 2 Color"
                      name="h2Color"
                    />
                    <ColorFormField
                      label="Header 3 Color"
                      name="h3Color"
                    />
                    <ColorFormField
                      label="Header 4 Color"
                      name="h4Color"
                    />
                    <ColorFormField
                      label="Header 5 Color"
                      name="h5Color"
                    />
                    <ColorFormField
                      label="Header 6 Color"
                      name="h6Color"
                    />
                    <Flex>
                      <Flex.Item grow={1}>
                        <TextFormField
                          label="H1 Font Size"
                          secondaryText="Please specify em as the unit"
                          name="h1FontSize"
                        />
                      </Flex.Item>
                      <Flex.Item grow={1}>
                        <TextFormField
                          label="H1 Font Weight"
                          caption="E.G. 100,200..900, normal, bold, bolder, lighter"
                          secondaryText="Please specify a valid value"
                          name="h1FontWeight"
                        />
                      </Flex.Item>
                    </Flex>
                    <Flex>
                      <Flex.Item grow={1}>
                        <TextFormField
                          label="H2 Font Size"
                          secondaryText="Please specify em as the unit"
                          name="h2FontSize"
                        />
                      </Flex.Item>
                      <Flex.Item grow={1}>
                        <TextFormField
                          label="H2 Font Weight"
                          caption="E.G. 100,200..900, normal, bold, bolder, lighter"
                          secondaryText="Please specify a valid value"
                          name="h2FontWeight"
                        />
                      </Flex.Item>
                    </Flex>
                    <Flex>
                      <Flex.Item grow={1}>
                        <TextFormField
                          label="H3 Font Size"
                          secondaryText="Please specify em as the unit"
                          name="h3FontSize"
                        />
                      </Flex.Item>
                      <Flex.Item grow={1}>
                        <TextFormField
                          label="H3 Font Weight"
                          caption="E.G. 100,200..900, normal, bold, bolder, lighter"
                          secondaryText="Please specify a valid value"
                          name="h3FontWeight"
                        />
                      </Flex.Item>
                    </Flex>
                    <Flex>
                      <Flex.Item grow={1}>
                        <TextFormField
                          label="H4 Font Size"
                          secondaryText="Please specify em as the unit"
                          name="h4FontSize"
                        />
                      </Flex.Item>
                      <Flex.Item grow={1}>
                        <TextFormField
                          label="H4 Font Weight"
                          caption="E.G. 100,200..900, normal, bold, bolder, lighter"
                          secondaryText="Please specify a valid value"
                          name="h4FontWeight"
                        />
                      </Flex.Item>
                    </Flex>
                    <Flex>
                      <Flex.Item grow={1}>
                        <TextFormField
                          label="H5 Font Size"
                          secondaryText="Please specify em as the unit"
                          name="h5FontSize"
                        />
                      </Flex.Item>
                      <Flex.Item grow={1}>
                        <TextFormField
                          label="H5 Font Weight"
                          caption="E.G. 100,200..900, normal, bold, bolder, lighter"
                          secondaryText="Please specify a valid value"
                          name="h5FontWeight"
                        />
                      </Flex.Item>
                    </Flex>
                    <Flex>
                      <Flex.Item grow={1}>
                        <TextFormField
                          label="H6 Font Size"
                          secondaryText="Please specify em as the unit"
                          name="h6FontSize"
                        />
                      </Flex.Item>
                      <Flex.Item grow={1}>
                        <TextFormField
                          label="H6 Font Weight"
                          caption="E.G. 100,200..900, normal, bold, bolder, lighter"
                          secondaryText="Please specify a valid value"
                          name="h6FontWeight"
                        />
                      </Flex.Item>
                    </Flex>
                  </FormFieldset>
                </Tabs.Tab>
                <Tabs.Tab title="Typography">
                  <FormFieldset legend="General">
                    <ColorFormField
                      label="Color"
                      caption="E.G. Text color"
                      name="color"
                    />
                  </FormFieldset>
                  <FormFieldset legend="Theme">
                    <ColorFormField
                      label="Color Theme"
                      caption="E.G. Text color on regular buttons"
                      name="colorTheme"
                    />
                    <ColorFormField
                      label="Color Theme Focus"
                      caption="E.G. Side navigation bar text color when tabbing through items"
                      name="colorThemeFocus"
                    />
                    <ColorFormField
                      label="Color Theme Hover"
                      caption="E.G. Side navigation bar text color when mouse hovering"
                      name="colorThemeHover"
                    />
                  </FormFieldset>
                  <FormFieldset legend="Family">
                    <TextFormField
                      label="Font Family"
                      caption={'The primary font family. Default value (including quotes): "Open Sans"'}
                      secondaryText="If you have a space in the font name you must surround it with double quotes"
                      name="fontFamily"
                    />
                  </FormFieldset>
                  <FormFieldset legend="Size">
                    <TextFormField
                      label="Font Size Base"
                      caption="E.G. Plan details text. Changing this value will affect the UI layout spacing as well. Default value: 16px"
                      secondaryText="Please specify px as the unit"
                      name="fontSizeBase"
                    />
                    <TextFormField
                      label="Font size UI"
                      secondaryText="Please specify em as the unit"
                      name="fontSizeUI"
                    />
                    <TextFormField
                      label="Font Size Prose"
                      secondaryText="Please specify em as the unit"
                      name="fontSizeProse"
                    />
                    <TextFormField
                      label="Line Height"
                      caption="E.G. Sets the distance between lines of a text."
                      secondaryText="Please specify em as the unit"
                      name="lineHeight"
                    />
                    <TextFormField
                      label="Line Height Heading"
                      caption="E.G. Sets the distance between lines of a heading."
                      secondaryText="Please specify em as the unit"
                      name="lineHeightHeading"
                    />
                    <TextFormField
                      label="Line Height Prose"
                      secondaryText="Please specify em as the unit"
                      name="lineHeightProse"
                    />
                  </FormFieldset>
                </Tabs.Tab>
                <Tabs.Tab title="Space">
                  <FormFieldset legend="Inline">
                    <TextFormField
                      label="Space Inline Small"
                      secondaryText="Please specify em as the unit"
                      name="spaceInlineSM"
                    />
                    <TextFormField
                      label="Space Inline Medium"
                      secondaryText="Please specify em as the unit"
                      name="spaceInlineMD"
                    />
                    <TextFormField
                      label="Space Inline Large"
                      secondaryText="Please specify em as the unit"
                      name="spaceInlineLG"
                    />
                  </FormFieldset>
                  <FormFieldset legend="Stack">
                    <TextFormField
                      label="Space Stack Small"
                      secondaryText="Please specify em as the unit"
                      name="spaceStackSM"
                    />
                    <TextFormField
                      label="Space Stack Medium"
                      secondaryText="Please specify em as the unit"
                      name="spaceStackMD"
                    />
                    <TextFormField
                      label="Space Stack Large"
                      secondaryText="Please specify em as the unit"
                      name="spaceStackLG"
                    />
                  </FormFieldset>
                </Tabs.Tab>
              </Tabs>
              <Flex paddingTop="1em">
                <Flex.Item marginStart="auto">
                  <ButtonGroup>
                    <Button type="button" onClick={() => this.setState({ saveAllOpen: true })}>Save to All Carriers</Button>
                    <Button primary type="submit" loading={saving}>Save</Button>
                  </ButtonGroup>
                </Flex.Item>
              </Flex>
            </form>
          </Card.Block>
        </Card>
        <Modal title="Save to All Carriers" isOpen={saveAllOpen} onClose={() => this.setState({ saveAllOpen: false })}>
          <Modal.Body>
            <Text as="h5">
              {`Are you sure you want update all carrier themes for ${client.clientName}?`}
            </Text>
          </Modal.Body>
          <Modal.Footer>
            <ButtonGroup>
              <Button minimal onClick={() => this.setState({ saveAllOpen: false })}>
                Cancel
              </Button>
              <Button
                primary
                loading={saving}
                onClick={() => {
                  this.saveTheme(true)
                    .then(() => this.setState({ saveAllOpen: false }));
                }}
              >
                Save All
              </Button>
            </ButtonGroup>
          </Modal.Footer>
        </Modal>
      </Page>
    );
  }
}

function mapStateToProps(state) {
  return {
    client: getClient(state),
    fontSizeOverride: getFontSizeOverride(state),
    themeWithoutOverrides: getThemeWithoutOverrides(state),
  };
}
Theme.propTypes = propTypes;
Theme.defaultProps = defaultProps;
export default connect(mapStateToProps)(withTheme(Theme));
